import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';

import { Observable, ReplaySubject } from 'rxjs';

import { catchError, filter, map, take, tap } from 'rxjs/operators';

interface CacheConfigItem {
    url: RegExp;
    TTL?: number;
}

export const CACHE_CONFIG = new InjectionToken<CacheConfigItem[]>('CACHE_CONFIG');

interface CacheItem<T> {
    Item: ReplaySubject<T>;
    Dirty: boolean;
    LastFetched: Date;
}

@Injectable({
    providedIn: 'root'
})
export class ShCacheInterceptor implements HttpInterceptor {
    private readonly CACHE_CONFIG: CacheConfigItem[];
    private readonly DEFAULT_CACHE_TIMEOUT: number = 1800000; // 30 minutes;
    private readonly UUID_REGEX: RegExp =
        /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

    private cache: Map<string, CacheItem<any>> = new Map();

    constructor() {
        try {
            // Angular will throw an circular dependency error when this fails for any reason because logic
            this.CACHE_CONFIG = inject(CACHE_CONFIG);
            if (!this.CACHE_CONFIG.length) {
                throw new Error('No cache route whitelist provided. Not caching anything.');
            }
        } catch {
            console.warn('No cache route whitelist provided. Not caching anything.');
        }
    }

    public clearCache(): void {
        this.cache = new Map();
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        for (const configItem of this.CACHE_CONFIG) {
            if (request.urlWithParams.match(configItem.url)) {
                switch (request.method) {
                    case 'GET':
                        return this.handleGet(request, next, configItem.TTL ?? this.DEFAULT_CACHE_TIMEOUT);
                    case 'PUT':
                        return this.handlePut(request, next);
                    case 'POST':
                        return this.handlePost(request, next);
                    case 'DELETE':
                        return this.handleDelete(request, next);
                }
            }
        }

        return next.handle(request);
    }

    private handleGet(request: HttpRequest<any>, next: HttpHandler, TTL: number): Observable<HttpEvent<any>> {
        const cacheItem: CacheItem<any> | undefined = this.getCacheItemFromCache(request, TTL);

        if (cacheItem) {
            return cacheItem.Item.pipe(
                map(item => new HttpResponse({ body: item })),
                take(1)
            );
        }

        const placeholder: CacheItem<any> = this.addCachePlaceHolder(request.urlWithParams);
        next.handle(request)
            .pipe(
                filter((response): response is HttpResponse<any> => this.isHttpResponse(response)),
                take(1),
                catchError(e => {
                    this.cache.delete(request.urlWithParams);
                    placeholder.Item.error(e);
                    placeholder.Item.complete();
                    throw e; // Make sure downstream error handling can still catch the error
                })
            )
            .subscribe(result => placeholder.Item.next(result.body));

        return placeholder.Item.pipe(
            map(item => new HttpResponse({ body: item })),
            take(1)
        );
    }

    private handlePut(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            tap(() => {
                const dirtyCandidates: string[] = this.getMarkDirtyPaths(request.url);
                this.cache.forEach((value, key) => {
                    if (
                        dirtyCandidates.some(dirtyCandidate => new URL(key).pathname.endsWith(dirtyCandidate)) &&
                        !key.includes('users/current')
                    ) {
                        // console.info('Marked dirty: ', key);
                        value.Dirty = true;
                    }
                });
            })
        );
    }

    private handlePost(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            tap(response => {
                if (this.isHttpResponse(response) && response.status === 201) {
                    // HTTP code Created

                    const dirtyCandidates: string[] = this.getMarkDirtyPaths(request.url);
                    this.cache.forEach((value, key) => {
                        if (
                            dirtyCandidates.some(dirtyCandidate => new URL(key).pathname.endsWith(dirtyCandidate)) &&
                            !key.includes('users/current')
                        ) {
                            // console.info('Marked', key);
                            value.Dirty = true;
                        }
                    });
                }
            })
        );
    }

    private handleDelete(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            tap(() => {
                const dirtyCandidates: string[] = this.getMarkDirtyPaths(request.url);
                this.cache.forEach((value, key) => {
                    if (
                        dirtyCandidates.some(dirtyCandidate => new URL(key).pathname.endsWith(dirtyCandidate)) &&
                        !key.includes('users/current')
                    ) {
                        const cacheItem: ReplaySubject<any> = value.Item;
                        cacheItem.pipe(take(1)).subscribe(() => cacheItem.complete()); // Make sure it emits once

                        // console.info('Removed', key);
                        // Just delete everything since this is essentially the same thing as marking it
                        this.cache.delete(key);
                    }
                });
            })
        );
    }

    private getCacheItemFromCache<T>(request: HttpRequest<any>, TTL: number): CacheItem<T> | undefined {
        const cacheItem: CacheItem<T> | undefined = this.cache.get(request.urlWithParams);
        if (cacheItem && !cacheItem.Dirty) {
            // console.info('Cache match', request.urlWithParams);
            if (new Date().getTime() - cacheItem.LastFetched.getTime() < TTL) {
                return cacheItem; // Item is neither dirty, nor outdated -> return cached value
            }
        }

        return undefined;
    }

    /**
     * Adds a placeholder in the cache to ensure later calls don't start their own requests while the previous one is still processing
     * @returns The placeholder (as CacheItem)
     */
    private addCachePlaceHolder<T>(url: string): CacheItem<T> {
        const newCacheItem: CacheItem<T> = {
            Item: new ReplaySubject<T>(1),
            Dirty: false,
            LastFetched: new Date()
        };

        const oldCacheItem: CacheItem<T> | undefined = this.cache.get(url);
        if (oldCacheItem) {
            oldCacheItem.Item.pipe(take(1)).subscribe(
                () => oldCacheItem.Item.complete() // Properly terminate old value, but only after it has emitted once (to prevent issues with overlaps)
            );
        }
        this.cache.set(url, newCacheItem);

        return newCacheItem;
    }

    // #endregion

    // #region Misc util
    private isHttpResponse(response: HttpEvent<any>): response is HttpResponse<any> {
        return typeof response === 'object' && (response as any).body;
    }

    private getMarkDirtyPaths(url: string): string[] {
        const parsedUrl: URL = new URL(url);
        const segments: string[] = parsedUrl.pathname.split('/');

        // Number of layers above the cache will attempt mark dirty
        // Reference: profiles (/profiles/:profileId/profileFields/:DottedKey -> 4 layers)
        const updateDepth: number = 4;

        if (
            !Number.isNaN(+segments.at(-1)!) ||
            this.UUID_REGEX.exec(segments.at(-1)!) ||
            segments.at(-2) === 'defaults' ||
            segments.at(-1)?.includes('.') // DottedKey
        ) {
            return segments
                .slice(-updateDepth)
                .filter(segment => segment && segment !== '/')
                .reduce(
                    (acc: string[], segment) => acc.concat(acc.length ? [`${acc.at(-1)}/${segment}`] : [`/${segment}`]),
                    []
                );
        }

        return segments
            .slice(-(updateDepth - 1))
            .reduce(
                (acc: string[], segment) => acc.concat(acc.length ? [`${acc.at(-1)}/${segment}`] : [`/${segment}`]),
                []
            );
    }

    // #endregion
}
